import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  title,
  children,
  onClose
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-id="element-53">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.25
    }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-54">
        <div className="sticky top-0 bg-white border-b border-sage/20 p-6 flex justify-between items-center rounded-t-2xl" data-id="element-55">
          <h3 className="text-2xl font-serif text-teal" data-id="element-56">{title}</h3>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-cream transition-colors" aria-label="Close" data-id="element-57">
            <XIcon className="w-5 h-5 text-charcoal" data-id="element-58" />
          </button>
        </div>

        <div className="p-6 md:p-8 text-charcoal/80 leading-relaxed space-y-4 text-sm" data-id="element-59">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-white/60 py-8 text-center text-sm" data-id="element-60">
        <p data-id="element-61">
          © {new Date().getFullYear()} The 7-Day Menopause Reset. All rights
          reserved.
        </p>
        <p className="mt-2 text-white/40 max-w-2xl mx-auto px-6 text-xs" data-id="element-62">
          Disclaimer: The information provided is for educational purposes only
          and is not intended as a substitute for advice from your physician or
          other health care professional.
        </p>
        <div className="mt-4 flex justify-center gap-6" data-id="element-63">
          <button onClick={() => setActiveModal('privacy')} className="text-white/50 hover:text-white transition-colors underline underline-offset-2" data-id="element-64">
            Privacy Policy
          </button>
          <button onClick={() => setActiveModal('terms')} className="text-white/50 hover:text-white transition-colors underline underline-offset-2" data-id="element-65">
            Terms of Service
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-66">
        {activeModal === 'privacy' && <Modal title="Privacy Policy" onClose={() => setActiveModal(null)} data-id="element-67">
            <p data-id="element-68">
              <strong data-id="element-69">Effective Date:</strong> January 1, 2025
            </p>
            <p data-id="element-70">
              Your privacy is important to us. This Privacy Policy explains how
              we collect, use, and protect your personal information when you
              use our website and purchase our digital products.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-71">
              Information We Collect
            </h4>
            <p data-id="element-72">We may collect the following types of information:</p>
            <ul className="list-disc pl-5 space-y-1" data-id="element-73">
              <li data-id="element-74">Name and email address when you make a purchase</li>
              <li data-id="element-75">
                Payment information (processed securely through third-party
                payment processors)
              </li>
              <li data-id="element-76">
                Usage data such as pages visited and time spent on the site
              </li>
            </ul>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-77">
              How We Use Your Information
            </h4>
            <ul className="list-disc pl-5 space-y-1" data-id="element-78">
              <li data-id="element-79">To deliver your purchased digital products</li>
              <li data-id="element-80">To send order confirmations and product updates</li>
              <li data-id="element-81">To improve our website and user experience</li>
            </ul>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-82">
              Data Protection
            </h4>
            <p data-id="element-83">
              We implement industry-standard security measures to protect your
              personal information. We do not sell, trade, or share your
              personal data with third parties for marketing purposes.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-84">
              Cookies
            </h4>
            <p data-id="element-85">
              Our website may use cookies to enhance your browsing experience.
              You can disable cookies in your browser settings at any time.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-86">
              Your Rights
            </h4>
            <p data-id="element-87">
              You have the right to access, correct, or delete your personal
              data at any time. To make a request, please reach out through the
              website.
            </p>
          </Modal>}

        {activeModal === 'terms' && <Modal title="Terms of Service" onClose={() => setActiveModal(null)} data-id="element-88">
            <p data-id="element-89">
              <strong data-id="element-90">Effective Date:</strong> January 1, 2025
            </p>
            <p data-id="element-91">
              By accessing and using this website and purchasing our digital
              products, you agree to the following terms and conditions.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-92">
              Digital Products
            </h4>
            <p data-id="element-93">
              All products sold on this website are digital downloads delivered
              electronically. Upon purchase, you will receive instant access to
              your product. Due to the digital nature of the product, all sales
              are final.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-94">
              Intellectual Property
            </h4>
            <p data-id="element-95">
              All content, including text, graphics, and digital materials, is
              the intellectual property of The 7-Day Menopause Reset. You may
              not reproduce, distribute, or resell any content without written
              permission.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-96">
              Health Disclaimer
            </h4>
            <p data-id="element-97">
              The information provided in our products is for educational
              purposes only and is not intended to replace professional medical
              advice, diagnosis, or treatment. Always consult your physician
              before starting any new health program.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-98">
              Limitation of Liability
            </h4>
            <p data-id="element-99">
              We are not liable for any damages arising from the use of our
              products or website. Results may vary and are not guaranteed.
            </p>

            <h4 className="font-serif text-teal text-base font-semibold mt-4" data-id="element-100">
              Changes to Terms
            </h4>
            <p data-id="element-101">
              We reserve the right to update these terms at any time. Continued
              use of the website constitutes acceptance of any changes.
            </p>
          </Modal>}
      </AnimatePresence>
    </>;
}